import { Button, TextField, SkeletonThumbnail, Text, Thumbnail } from '@shopify/polaris';
import { CheckIcon, DeleteIcon, EditIcon, XSmallIcon } from '@shopify/polaris-icons';
import { getProductInfo } from '#client/pages/PurchaseOrders/utils/helpers';
import { useEffect } from 'react';

export default function DataTableRows({
    variants,
    editingIndex,
    editingQty,
    handleEditClick,
    handleQtyChange,
    handleCancelEdit,
    purchaseOrder,
    handleManualUpdateOfTotal,
    displayStatus,
    lastAdded,
    handleRemove,
    scannedVariants,
    hasTriedToNavigateRows,
    markRowTriedToNavigate,
}) {
    // Common styles
    const editingStyle = { opacity: 0.5 };
    const normalStyle = {};

    const getRowClass = (entry, isOverreceived, isLastAdded) => {
        let classes = ['product-title-and-image'];
        if (entry.qtyReceived === 0) {
            classes.push('zero-quantity-received-row');
        } else if (isOverreceived === true || entry.productOnPurchaseOrder === false) {
            classes.push('overreceived-row');
        } else if (isLastAdded) {
            classes.push('row-flash-green');
        }

        return classes.join(' ');
    };

    const createItemId = (entry) => {
        const sku = entry.variant?.sku || entry.sku || '';
        const barcode = entry.barcode || entry.variant?.barcode || '';

        if (sku && barcode) {
            return `${sku}-${barcode}`;
        } else if (sku) {
            return `sku-${sku}`;
        } else if (barcode) {
            return `barcode-${barcode}`;
        }
    };

    // Handle reload attempts while editing
    useEffect(() => {
        const handleBeforeUnload = () => {
            // If currently editing, mark this row as having tried to navigate
            if (editingIndex !== null) {
                const entry = variants[editingIndex];

                if (entry) {
                    const key = createItemId(entry);
                    markRowTriedToNavigate(key);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [editingIndex, markRowTriedToNavigate, variants]);

    // Check to force the user out of editing mode if the displayStatus is 'Completed'
    useEffect(() => {
        if (displayStatus === 'Completed' && editingIndex !== null) {
            handleCancelEdit();
        }
    }, [displayStatus, editingIndex, handleCancelEdit]);

    const isLastAddedItem = (entry) => {
        return lastAdded && entry.barcode === lastAdded.barcode && entry.variant?.sku === lastAdded.sku;
    };

    const getEditingValue = (index, entry) => {
        return editingQty[index] ?? entry.qtyReceived;
    };

    const isValidQty = (index, entry) => {
        const value = String(getEditingValue(index, entry)).trim();
        return value !== '' && !isNaN(Number(value));
    };

    const renderProductImage = (entry) => {
        const thumbnailSrc = entry.variant.variantImage;

        return thumbnailSrc ? <Thumbnail source={thumbnailSrc} alt={'Product Image'} size="small" /> : <SkeletonThumbnail size="small" />;
    };

    const handleDeleteClick = (event, entry) => {
        event.stopPropagation();
        event.preventDefault();

        const originalArray = scannedVariants || variants;
        const productIndex = originalArray.findIndex((variant) => variant.variant?.sku === entry.variant?.sku && variant.barcode === entry.barcode);

        // Validate that the index is found in the original array, and if so, call the handleRemove function
        if (productIndex !== -1) {
            handleRemove(productIndex);
        }
    };

    const renderEditingControls = (index, entry) => {
        // Apply red border styling when:
        // User is currently editing this specific row AND this row has previously tried to navigate
        const key = createItemId(entry);
        const isCurrentlyEditing = editingIndex === index;
        const hasThisRowTriedToNavigate = hasTriedToNavigateRows.has(key);
        const shouldShowWarning = isCurrentlyEditing && hasThisRowTriedToNavigate;

        const unsavedEditStyle = shouldShowWarning ? { border: '2px solid #e53e3e', borderRadius: '4px', padding: '4px' } : {};

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...unsavedEditStyle }}>
                {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                <div
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            if (isValidQty(index, entry)) {
                                handleManualUpdateOfTotal(index, purchaseOrder);
                            }
                        }
                    }}
                >
                    <TextField value={getEditingValue(index, entry)} onChange={(val) => handleQtyChange(val, index)} autoFocus type="text" min="0" labelHidden style={{ width: 60 }} />
                </div>

                <Button
                    icon={CheckIcon}
                    variant="monochromePlain"
                    onClick={() => {
                        handleManualUpdateOfTotal(index, purchaseOrder);
                    }}
                    accessibilityLabel="Save"
                    disabled={!isValidQty(index, entry)}
                />
                <Button icon={XSmallIcon} variant="monochromePlain" onClick={handleCancelEdit} accessibilityLabel="Cancel" />
            </div>
        );
    };

    const renderActionButtons = (index, entry, isEditing) => {
        if (displayStatus === 'Completed') return null;

        const showDelete = entry.productOnPurchaseOrder === false || Number(entry.qtyReceived) === 0;

        return (
            <>
                <Button icon={EditIcon} variant="plain" onClick={() => handleEditClick(index, entry.qtyReceived)} disabled={isEditing} />

                {showDelete && <Button icon={DeleteIcon} variant="plain" onClick={(event) => handleDeleteClick(event, entry)} accessibilityLabel="Remove" destructive />}
            </>
        );
    };

    return variants.map((entry, index) => {
        const isEditing = editingIndex === index;
        const isOverreceived = entry.quantityReceived > entry.quantityExpected;
        const isLastAdded = isLastAddedItem(entry, lastAdded); // Pass lastAdded as parameter
        const cellStyle = isEditing ? editingStyle : normalStyle;
        const rowClass = getRowClass(entry, isOverreceived, isLastAdded);
        const productInfo = getProductInfo(entry);

        return [
            <div key={`product-${entry.variant?.sku}-${entry.barcode}`} className={rowClass}>
                {renderProductImage(entry, index)}
                <div style={{ marginLeft: 4 }}>
                    <Text as="span" variant="headingSm" fontWeight={productInfo.textWeight} tone={productInfo.tone}>
                        {productInfo.productName}
                    </Text>
                </div>
            </div>,
            <span key={`product-title-${entry.variant?.sku}-${index}`} style={cellStyle}>
                {entry.variant?.title !== 'Default Title' ? entry.variant?.title : ''}
            </span>,
            <span key={`product-sku-${entry.variant?.sku}-${index}`} style={cellStyle}>
                {entry.variant?.sku}
            </span>,
            <span key={`product-barcode-${entry.variant?.sku}-${index}`} style={cellStyle}>
                {entry.variant?.barcode}
            </span>,
            <span key={`product-qty-expect-${entry.variant?.sku}-${index}`} style={cellStyle}>
                {entry.quantityExpected ? entry.quantityExpected : 0}
            </span>,
            <span key={`product-qty-received-${entry.variant?.sku}-${index}`} style={cellStyle}>
                {entry.quantityReceived}
            </span>,
            isEditing ? renderEditingControls(index, entry) : entry.qtyReceived,
            renderActionButtons(index, entry, isEditing),
        ];
    });
}
